import type React from 'react';
import { useEffect, useRef } from 'react';

import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { useMediaQuery } from '#src/app/shared/hooks/dom';
import { isMotionReduced } from '#src/app/shared/utils/accessibility';

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

/**
 * Turns on the player's own autoplay. TikTok mutes the video when it autoplays and keeps the
 * volume control usable, so the viewer can still unmute (unlike the `muted` option, which locks
 * it). The player only starts posting its events, `onPlayerReady` included, once autoplay is on,
 * so starting it from the host page through postMessage doesn't work.
 */
function withAutoplay(playerUrl: string): string {
	try {
		const url = new URL(playerUrl);
		url.searchParams.set('autoplay', '1');
		return url.toString();
	} catch {
		return playerUrl;
	}
}

function isPlayerError(data: unknown): boolean {
	return (
		typeof data === 'object' && data !== null && 'x-tiktok-player' in data && 'type' in data && data.type === 'onError'
	);
}

const TikTokPlayerClient: React.FC<Props> = ({ playerUrl, title, viewport, autoplayMuted = false, onError }) => {
	const initialIsDesktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
	const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY, initialIsDesktop);
	const matchesViewport = !viewport || viewport === (isDesktop ? 'desktop' : 'mobile');
	const iframeRef = useRef<HTMLIFrameElement>(null);

	// The player posts an `onError` event when the video can't play (deleted, private, or blocked).
	useEffect(() => {
		if (!onError) return undefined;
		const listener = (event: MessageEvent) => {
			if (event.source !== iframeRef.current?.contentWindow || !isPlayerError(event.data)) return;
			onError();
		};
		window.addEventListener('message', listener);
		return () => window.removeEventListener('message', listener);
	}, [onError]);

	if (!matchesViewport) return null;

	return (
		<iframe
			ref={iframeRef}
			src={autoplayMuted && !isMotionReduced() ? withAutoplay(playerUrl) : playerUrl}
			title={title}
			className="h-full w-full border-0"
			allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
			allowFullScreen
			referrerPolicy="strict-origin-when-cross-origin"
		/>
	);
};

export const TikTokPlayer: React.FC<Props> = (props) => {
	return <ClientRender>{() => <TikTokPlayerClient {...props} />}</ClientRender>;
};

type Props = {
	playerUrl: string;
	title: string;
	/**
	 * Mounts the player only on this viewport, for pages that render separate mobile and desktop
	 * layouts at once. Leave it out to always mount it.
	 */
	viewport?: 'desktop' | 'mobile';
	/** Starts the video muted as soon as it loads (the viewer can unmute it). Skipped under reduced motion. */
	autoplayMuted?: boolean;
	/** Called when the player reports that the video can't play, so the page can show something else. */
	onError?: () => void;
};
